import { useState, useMemo, useRef } from 'react';
import { Spinner, Input, Modal, ModalBody, ModalFooter, Button, Label } from 'reactstrap';
import {
  Search,
  ChevronDown,
  ChevronUp,
  CheckSquare,
  Square,
  Download,
  CheckCircle,
  AlertCircle,
  FileText,
  RotateCcw,
} from 'react-feather';
import Table from '../ui/Table';
import ModalLoader from '../../layouts/modals/ModalLoader';
import colors from '../../theme/colors';
import presupuestosApiService from '../../services/http/presupuestosApiService';
import logger from '../../services/logger';

const EMPTY_FILTERS = {
  id: '',
  nombre: '',
  apellidos: '',
  email: '',
  telefono: '',
  ciudad: '',
  empresa: '',
};

const PAGE_LIMIT = 100;
const MAX_PAGES = 200;

const CENTROS = ['CEDESCA', 'CNTEC'];

// Devuelve el centro normalizado (CEDESCA|CNTEC) o null si el contacto no tiene
// uno valido (vacio, "SIN ASIGNAR" o cualquier otra empresa de HubSpot).
const normalizaCentro = (empresa) => {
  const valor = (empresa || '').trim().toUpperCase();
  return CENTROS.includes(valor) ? valor : null;
};

const mapContacto = (c) => ({
  id: c.id,
  Nombre: c.Nombre || '',
  Apellidos: c.Apellidos || '',
  Email: c.Email || '',
  Telefono: c.Telefono || '',
  Ciudad: c.Ciudad || '',
  Empresa: c.Empresa || '',
  FechaCreacion: c.FechaCreacion ? new Date(c.FechaCreacion).toLocaleDateString('es-ES') : '',
});

const ContactosHubspot = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [selected, setSelected] = useState([]);
  const [showFilters, setShowFilters] = useState(true);
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  const [isImporting, setIsImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [importError, setImportError] = useState('');
  // Contactos seleccionados sin centro valido: hay que asignarlo a mano antes
  // de importar. asignaciones = { [id]: 'CEDESCA' | 'CNTEC' | '' }.
  const [pendientesCentro, setPendientesCentro] = useState([]);
  const [asignaciones, setAsignaciones] = useState({});

  const cancelLoadRef = useRef(false);

  const handleFilterChange = (e) => {
    setFilters((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const limpiarFiltros = () => setFilters(EMPTY_FILTERS);

  const empresaMatches = (empresaContacto, empresaFiltro) => {
    if (!empresaFiltro) return true;
    const valor = (empresaContacto || '').trim().toUpperCase();
    if (empresaFiltro === 'SIN_ASIGNAR') return valor === '' || valor === 'SIN ASIGNAR';
    return valor === empresaFiltro;
  };

  const filteredResults = useMemo(() => {
    const norm = (v) => (v || '').toString().trim().toLowerCase();
    return results.filter((c) => {
      if (filters.id && !norm(c.id).includes(norm(filters.id))) return false;
      if (filters.nombre && !norm(c.Nombre).includes(norm(filters.nombre))) return false;
      if (filters.apellidos && !norm(c.Apellidos).includes(norm(filters.apellidos))) return false;
      if (filters.email && !norm(c.Email).includes(norm(filters.email))) return false;
      if (filters.telefono && !norm(c.Telefono).includes(norm(filters.telefono))) return false;
      if (filters.ciudad && !norm(c.Ciudad).includes(norm(filters.ciudad))) return false;
      if (!empresaMatches(c.Empresa, filters.empresa)) return false;
      return true;
    });
  }, [results, filters]);

  const handleSelect = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));
  };

  const handleSelectAll = () => {
    const visibleIds = filteredResults.map((r) => r.id);
    const allVisibleSelected = visibleIds.every((id) => selected.includes(id));
    if (allVisibleSelected) {
      setSelected((prev) => prev.filter((id) => !visibleIds.includes(id)));
    } else {
      setSelected((prev) => Array.from(new Set([...prev, ...visibleIds])));
    }
  };

  // Importa la lista ya resuelta (cada contacto con su centro en Empresa).
  const ejecutarImport = async (contactos) => {
    setIsImporting(true);
    setImportError('');
    try {
      const grouped = {};
      contactos.forEach((c) => {
        const hq = normalizaCentro(c.Empresa) || 'CEDESCA';
        if (!grouped[hq]) grouped[hq] = [];
        grouped[hq].push(c);
      });
      const responses = await Promise.all(
        Object.entries(grouped).map(([headquarter, lista]) =>
          presupuestosApiService.importarContactosHubspot({ headquarter, contactos: lista }),
        ),
      );
      const allResults = responses.flat();
      const importedIds = allResults.filter((r) => r.status === 'imported').map((r) => r.id);
      setResults((prev) => prev.filter((r) => !importedIds.includes(r.id)));
      setImportResults(allResults);
      setSelected([]);
    } catch (error) {
      logger.error('Error importando contactos:', error);
      setImportError('Error al importar los contactos.');
    } finally {
      setIsImporting(false);
    }
  };

  const handleImport = () => {
    const contactosToImport = results.filter((r) => selected.includes(r.id));
    // Los que no tienen centro valido (CEDESCA/CNTEC) deben asignarse a mano
    // antes de crear el lead, en vez de fallar en el backend.
    const sinCentro = contactosToImport.filter((c) => !normalizaCentro(c.Empresa));
    if (sinCentro.length > 0) {
      setPendientesCentro(sinCentro);
      setAsignaciones(Object.fromEntries(sinCentro.map((c) => [c.id, ''])));
      return;
    }
    ejecutarImport(contactosToImport);
  };

  const asignarCentro = (id, centro) =>
    setAsignaciones((prev) => ({ ...prev, [id]: centro }));

  const asignarCentroATodos = (centro) =>
    setAsignaciones((prev) => Object.fromEntries(Object.keys(prev).map((id) => [id, centro])));

  const todosAsignados =
    pendientesCentro.length > 0 && pendientesCentro.every((c) => asignaciones[c.id]);

  const cancelarAsignacion = () => {
    setPendientesCentro([]);
    setAsignaciones({});
  };

  const confirmarAsignacion = () => {
    if (!todosAsignados) return;
    const contactosToImport = results
      .filter((r) => selected.includes(r.id))
      .map((c) => (asignaciones[c.id] ? { ...c, Empresa: asignaciones[c.id] } : c));
    setPendientesCentro([]);
    setAsignaciones({});
    ejecutarImport(contactosToImport);
  };

  const cargarTodos = async (startAfter) => {
    cancelLoadRef.current = false;
    setIsLoading(true);
    setLoadError('');
    setSearched(true);

    let after = startAfter;
    let pages = 0;
    let lastError = '';

    try {
      do {
        if (pages >= MAX_PAGES) {
          lastError =
            'Se alcanzó el tope de páginas (20.000 contactos). Verifica el cursor del backend.';
          break;
        }
        if (cancelLoadRef.current) break;

        // eslint-disable-next-line no-await-in-loop
        const data = await presupuestosApiService.listarContactosHubspot({
          limit: PAGE_LIMIT,
          after: after || undefined,
        });

        const batch = Array.isArray(data?.results) ? data.results : [];
        if (batch.length > 0) {
          setResults((prev) => [...prev, ...batch.map(mapContacto)]);
        }

        after = data?.paging?.next?.after || null;
        setNextCursor(after);
        pages += 1;
      } while (after);
    } catch (error) {
      logger.error('Error cargando contactos HubSpot:', error);
      lastError = 'Error al cargar contactos. Pulsa Reintentar para continuar desde el último punto.';
    } finally {
      if (lastError) setLoadError(lastError);
      setIsLoading(false);
    }
  };

  const handleSearch = () => {
    setResults([]);
    setSelected([]);
    setNextCursor(null);
    cargarTodos(null);
  };

  const handleRetry = () => {
    cargarTodos(nextCursor);
  };

  const allSelected =
    filteredResults.length > 0 && filteredResults.every((r) => selected.includes(r.id));
  const filtersActive = Object.values(filters).some((v) => v !== '');
  const btnStyle = (disabled) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    padding: '6px 14px',
    fontSize: '0.75rem',
    fontWeight: 600,
    borderRadius: '6px',
    border: '1px solid #e5e7eb',
    backgroundColor: '#fff',
    color: '#6b7280',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    transition: 'all 0.15s',
  });

  return (
    <>
      {isImporting && <ModalLoader header="Importando contactos..." />}
      {pendientesCentro.length > 0 && (
        <Modal isOpen centered size="lg">
          <ModalBody>
            <h5 style={{ marginBottom: '8px' }}>Asignar centro</h5>
            <p style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: 0 }}>
              {pendientesCentro.length} contacto(s) seleccionado(s) no tienen centro asignado.
              Indica a qué centro debe ir cada lead antes de importarlo.
            </p>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                margin: '8px 0 12px',
              }}
            >
              <Label style={{ fontSize: '0.75rem', color: '#6b7280', margin: 0 }}>
                Asignar a todos:
              </Label>
              <Input
                type="select"
                bsSize="sm"
                value=""
                style={{ width: 'auto' }}
                onChange={(e) => e.target.value && asignarCentroATodos(e.target.value)}
              >
                <option value="">—</option>
                {CENTROS.map((centro) => (
                  <option key={centro} value={centro}>
                    {centro}
                  </option>
                ))}
              </Input>
            </div>
            <table className="table table-sm" style={{ fontSize: '0.8rem' }}>
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Email</th>
                  <th style={{ width: '140px' }}>Centro</th>
                </tr>
              </thead>
              <tbody>
                {pendientesCentro.map((c) => (
                  <tr key={c.id}>
                    <td>{`${c.Nombre} ${c.Apellidos}`.trim() || c.id}</td>
                    <td>{c.Email || '—'}</td>
                    <td>
                      <Input
                        type="select"
                        bsSize="sm"
                        value={asignaciones[c.id] || ''}
                        onChange={(e) => asignarCentro(c.id, e.target.value)}
                        invalid={!asignaciones[c.id]}
                      >
                        <option value="">Selecciona…</option>
                        {CENTROS.map((centro) => (
                          <option key={centro} value={centro}>
                            {centro}
                          </option>
                        ))}
                      </Input>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" outline onClick={cancelarAsignacion}>
              Cancelar
            </Button>
            <Button color="primary" onClick={confirmarAsignacion} disabled={!todosAsignados}>
              Importar ({selected.length})
            </Button>
          </ModalFooter>
        </Modal>
      )}
      {importError && (
        <Modal isOpen centered>
          <ModalBody>
            <p className="text-danger">{importError}</p>
          </ModalBody>
          <ModalFooter>
            <Button color="primary" onClick={() => setImportError('')}>
              Cerrar
            </Button>
          </ModalFooter>
        </Modal>
      )}
      {importResults && (
        <Modal isOpen centered size="lg">
          <ModalBody>
            <h5 style={{ marginBottom: '16px' }}>Resultado de la importación de leads</h5>
            <table className="table table-sm" style={{ fontSize: '0.8rem' }}>
              <thead>
                <tr>
                  <th>Estado</th>
                  <th>Nombre</th>
                  <th>ID Lead</th>
                  <th>Mensaje</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {importResults.map((r) => {
                  const contact = results.find((res) => res.id === r.id);
                  const nombreCompleto = r.nombre || contact?.Nombre || r.id;
                  const isOk = r.status === 'imported';
                  const leadId =
                    r.Id_lead || r.idLead || r.id_lead || r.leadId || r.IdLead || r.ccodcli || r.id;
                  const empresa = (r.empresa || contact?.Empresa || 'CEDESCA').toUpperCase();
                  const handleCrearPresupuesto = () => {
                    if (!leadId) return;
                    localStorage.setItem(
                      'student',
                      JSON.stringify({
                        id: leadId,
                        nombre: contact?.Nombre || nombreCompleto.split(' ')[0] || '',
                        apellidos:
                          contact?.Apellidos || nombreCompleto.split(' ').slice(1).join(' ') || '',
                        empresaId: empresa,
                      }),
                    );
                    window.open('/presupuestos/nuevo?skipSearch=true', '_blank');
                  };
                  return (
                    <tr key={r.id}>
                      <td>
                        {isOk ? (
                          <CheckCircle size={16} color="#059669" />
                        ) : (
                          <AlertCircle size={16} color="#dc2626" />
                        )}
                      </td>
                      <td>{nombreCompleto}</td>
                      <td>{leadId || '—'}</td>
                      <td>
                        {isOk ? (
                          <span style={{ color: '#059669' }}>
                            Lead importado correctamente en {r.empresa || empresa}
                          </span>
                        ) : (
                          <span style={{ color: '#dc2626' }}>
                            {r.reason || 'No se ha podido importar'}
                          </span>
                        )}
                      </td>
                      <td>
                        {isOk && (
                          <Button
                            size="sm"
                            color="primary"
                            onClick={handleCrearPresupuesto}
                            disabled={!leadId}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              fontSize: '0.7rem',
                            }}
                          >
                            <FileText size={12} /> Crear presupuesto
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '8px', marginBottom: 0 }}>
              {importResults.filter((r) => r.status === 'imported').length} importado(s),{' '}
              {importResults.filter((r) => r.status !== 'imported').length} con errores o duplicados
            </p>
          </ModalBody>
          <ModalFooter>
            <Button color="primary" onClick={() => setImportResults(null)}>
              Cerrar
            </Button>
          </ModalFooter>
        </Modal>
      )}
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          boxShadow: '0 1px 3px rgb(0 0 0 / 0.06)',
          overflow: 'visible',
        }}
      >
        <div
          style={{
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: showFilters ? '1px solid #f3f4f6' : 'none',
          }}
        >
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.8rem',
              fontWeight: 600,
              color: '#374151',
              padding: '4px 8px',
              borderRadius: '6px',
            }}
          >
            <Search size={15} style={{ color: '#9ca3af' }} />
            Filtros
            {showFilters ? (
              <ChevronUp size={14} style={{ color: '#9ca3af' }} />
            ) : (
              <ChevronDown size={14} style={{ color: '#9ca3af' }} />
            )}
          </button>
          <div style={{ display: 'flex', gap: '8px' }}>
            {searched && results.length > 0 && (
              <>
                <button
                  type="button"
                  onClick={handleSelectAll}
                  disabled={isLoading}
                  style={btnStyle(isLoading)}
                >
                  {allSelected ? <Square size={13} /> : <CheckSquare size={13} />}
                  {allSelected ? 'Desmarcar todos' : 'Marcar todos'}
                </button>
                <button
                  type="button"
                  onClick={handleImport}
                  disabled={selected.length === 0 || isLoading || isImporting}
                  style={{
                    ...btnStyle(selected.length === 0 || isLoading || isImporting),
                    border: `1px solid ${colors.primary}`,
                    color: colors.primary,
                  }}
                >
                  <Download size={13} />
                  Importar ({selected.length})
                </button>
              </>
            )}
            <button
              type="button"
              onClick={limpiarFiltros}
              disabled={!filtersActive || isLoading}
              style={btnStyle(!filtersActive || isLoading)}
              title="Limpiar filtros"
            >
              <RotateCcw size={13} />
              Limpiar
            </button>
            <button
              type="button"
              onClick={handleSearch}
              disabled={isLoading}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                padding: '6px 14px',
                fontSize: '0.75rem',
                fontWeight: 600,
                borderRadius: '6px',
                border: 'none',
                backgroundColor: colors.primary,
                color: '#fff',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                opacity: isLoading ? 0.5 : 1,
                transition: 'all 0.15s',
              }}
            >
              {isLoading ? (
                <Spinner style={{ height: '13px', width: '13px' }} />
              ) : (
                <Search size={13} />
              )}
              Buscar
            </button>
          </div>
        </div>

        {showFilters && (
          <div style={{ padding: '16px' }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: '12px',
              }}
            >
              <div>
                <Label style={{ fontSize: '0.7rem', color: '#6b7280', marginBottom: '2px' }}>
                  ID
                </Label>
                <Input
                  type="text"
                  name="id"
                  value={filters.id}
                  onChange={handleFilterChange}
                  placeholder="ID"
                  bsSize="sm"
                />
              </div>
              <div>
                <Label style={{ fontSize: '0.7rem', color: '#6b7280', marginBottom: '2px' }}>
                  Nombre
                </Label>
                <Input
                  type="text"
                  name="nombre"
                  value={filters.nombre}
                  onChange={handleFilterChange}
                  placeholder="Nombre"
                  bsSize="sm"
                />
              </div>
              <div>
                <Label style={{ fontSize: '0.7rem', color: '#6b7280', marginBottom: '2px' }}>
                  Apellidos
                </Label>
                <Input
                  type="text"
                  name="apellidos"
                  value={filters.apellidos}
                  onChange={handleFilterChange}
                  placeholder="Apellidos"
                  bsSize="sm"
                />
              </div>
              <div>
                <Label style={{ fontSize: '0.7rem', color: '#6b7280', marginBottom: '2px' }}>
                  Email
                </Label>
                <Input
                  type="email"
                  name="email"
                  value={filters.email}
                  onChange={handleFilterChange}
                  placeholder="Email"
                  bsSize="sm"
                />
              </div>
              <div>
                <Label style={{ fontSize: '0.7rem', color: '#6b7280', marginBottom: '2px' }}>
                  Teléfono
                </Label>
                <Input
                  type="text"
                  name="telefono"
                  value={filters.telefono}
                  onChange={handleFilterChange}
                  placeholder="Teléfono"
                  bsSize="sm"
                />
              </div>
              <div>
                <Label style={{ fontSize: '0.7rem', color: '#6b7280', marginBottom: '2px' }}>
                  Ciudad
                </Label>
                <Input
                  type="text"
                  name="ciudad"
                  value={filters.ciudad}
                  onChange={handleFilterChange}
                  placeholder="Ciudad"
                  bsSize="sm"
                />
              </div>
              <div>
                <Label style={{ fontSize: '0.7rem', color: '#6b7280', marginBottom: '2px' }}>
                  Empresa
                </Label>
                <Input
                  type="select"
                  name="empresa"
                  value={filters.empresa}
                  onChange={handleFilterChange}
                  bsSize="sm"
                >
                  <option value="">Todas</option>
                  <option value="CEDESCA">CEDESCA</option>
                  <option value="CNTEC">CNTEC</option>
                  <option value="SIN_ASIGNAR">Sin asignar</option>
                </Input>
              </div>
            </div>
          </div>
        )}
      </div>

      {searched && (
        <div style={{ marginTop: '16px' }}>
          {(isLoading || loadError) && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px 12px',
                marginBottom: '12px',
                borderRadius: '6px',
                backgroundColor: loadError ? '#fef2f2' : '#f0f9ff',
                border: `1px solid ${loadError ? '#fecaca' : '#bae6fd'}`,
                fontSize: '0.75rem',
                color: loadError ? '#b91c1c' : '#075985',
              }}
              data-testid="hubspot-progress"
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                {isLoading && <Spinner style={{ height: '12px', width: '12px' }} />}
                {isLoading
                  ? `Cargando contactos… ${results.length} cargados`
                  : `${loadError} (${results.length} contactos cargados)`}
              </span>
              {loadError && !isLoading && nextCursor && (
                <button
                  type="button"
                  onClick={handleRetry}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '4px 10px',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    borderRadius: '4px',
                    border: `1px solid ${colors.primary}`,
                    backgroundColor: '#fff',
                    color: colors.primary,
                    cursor: 'pointer',
                  }}
                >
                  <RotateCcw size={12} /> Reintentar
                </button>
              )}
            </div>
          )}
          {filteredResults.length > 0 ? (
            <>
              {filtersActive && (
                <p style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '8px' }}>
                  Mostrando {filteredResults.length} de {results.length} contactos cargados
                </p>
              )}
              <Table
                columns={[
                  {
                    Header: '',
                    accessor: 'id',
                    id: 'select',
                    sortable: false,
                    width: 40,
                    /* eslint-disable react/prop-types */
                    Cell: ({ original }) => (
                      <Input
                        type="checkbox"
                        checked={selected.includes(original.id)}
                        onChange={() => handleSelect(original.id)}
                      />
                    ),
                    /* eslint-enable react/prop-types */
                  },
                  { Header: 'ID', accessor: 'id' },
                  { Header: 'Nombre', accessor: 'Nombre' },
                  { Header: 'Apellidos', accessor: 'Apellidos' },
                  { Header: 'Email', accessor: 'Email' },
                  { Header: 'Teléfono', accessor: 'Telefono' },
                  { Header: 'Ciudad', accessor: 'Ciudad' },
                  { Header: 'Empresa', accessor: 'Empresa' },
                  { Header: 'Fecha creación', accessor: 'FechaCreacion' },
                ]}
                data={filteredResults}
                defaultPageSize={20}
                showPaginationBottom={false}
                className="-striped -highlight text-center"
              />
            </>
          ) : (
            <p
              style={{
                color: '#6b7280',
                fontSize: '0.85rem',
                textAlign: 'center',
                marginTop: '24px',
              }}
            >
              {results.length > 0 && filtersActive
                ? 'Ningún contacto coincide con los filtros aplicados.'
                : 'No se han encontrado contactos.'}
            </p>
          )}
        </div>
      )}
    </>
  );
};

export default ContactosHubspot;
